import React from "react";
import moment from "moment-timezone";
import { Trans } from "react-i18next";

import * as genentechFreeDrugFieldNames from "./form/fieldNames";
import { getGenentechFreeDrugFormInitialValues } from "./form/initialValues";
import genentechFreeDrugFormJson from "./form/GenentechFreeDrugFormJson.json";
import renderReactFromJson from "../../utils/renderReactFromJson";
import { SaveButton } from "../../header";
import useBaseEEConfiguration from "../../hooks/useBaseEEConfiguration";
import { SignatureUploader } from "../../../PatientApplications/enrollTab/utils/uploadFunctionality";
import { fileTypes } from "../../../../Uploader";
import { CompleteFormButton } from "../../CompleteFormButton";
import { DATE_FORMAT } from "../../../../../utils/date";
import { APPLICATIONS } from "../../../../../constant/applications";
import { SubmittedFormModal } from "../../../PatientApplications/enrollTab/submission/SubmittedFormModal";
import { EEApproveApplicationMockAPI } from "../../../../../api/api";
import { ELIGIBILITY_QUESTIONNAIRE_FIELD_NAME, HEADER_SUBMIT_BUTTON_TYPES } from "../../constants";
import { applyTrimFields } from "../../utils/updateFormData";
import { SignerTypes } from "../../../../../constant/ESign";

const vendorName = "genentech";
const eePlanType = "free_drug";
const rootPath = "application_form_editor.genentech.free_drug";
const eligibilityQuestionnaire = "eligibilityQuestionnaire";

const validationFieldsBySidebarItem = {
  [eligibilityQuestionnaire]: [eligibilityQuestionnaire, "questionnaireOption"],
  patient: ["patient"],
  consent: ["consent"],
  insurance: ["insurance"],
  prescription: ["prescription"],
  medical: ["medical"],
  practice: ["practice"],
  submitAttestation: ["submitAttestation"]
};

const genentechSpecificConfig = {
  activeFormSection: "#enrollment-form-eligibilityQuestionnaire",
  fieldNames: genentechFreeDrugFieldNames,
  getFormInitialValues: getGenentechFreeDrugFormInitialValues,
  // The JSON renderer is the only render path (no getFormSectionComponents fallback), matching the
  // pfizer-copay (!4156) and gene-copay (!4219) migrations. The Patient section is declarative; the
  // stateful sections render as dedicated native field types (see GenentechFreeDrugFormJson.json).
  getFormFromJson: (form) => renderReactFromJson(form, genentechFreeDrugFormJson),
  applicationFormHeaderSubmission: (props) => <CompleteFormButton id={"EE_complete_button"} {...props} />,
  headerSubmitButtonType: HEADER_SUBMIT_BUTTON_TYPES.COMPLETE_FORM,
  successMessage: (t, submittedDate, shouldAutoSubmit, isEsign) => {
    return (
      <Trans
        i18nKey={
          isEsign
            ? "patientApplications.enroll.application_form_submission.submit_completed"
            : "patientApplications.enroll.application_form_submission.genentech_free_drug.submit_completed"
        }
        components={{
          br: <br />,
          submitDate: moment(submittedDate).format(DATE_FORMAT)
        }}
      />
    );
  },
  getViewDetailsModal: (onCancel, visible) => <SubmittedFormModal onCancel={onCancel} visible={visible} />,
  getShowEnrollResult: () => false,
  // Free-drug (PAP) never shows the copay result card, but it also inherits the base config's
  // getAdditionalResultCardInformation (a FUNCTION). EnrollResultCard passes that straight to t() as a
  // key, so any path that rendered the card without enrollData would show the raw function source.
  // Match the copay vendors and set it false (the PAP result never had a support-phone line).
  getAdditionalResultCardInformation: false,
  saveButton: (props) => <SaveButton onSave={onSaveGenentechForm} id={"EE_genentech_free_drug_save_form"} {...props} />,
  signatureStep: {
    uploadButtonText: (t) => t("patientApplications.enroll.electronic_signatures.upload_signature_button"),
    ESignButtonText: (t) => t("patientApplications.enroll.electronic_signatures.get_signature_button"),
    getDocusignRecipients: (t) => [
      { type: "provider", role: t("patientApplications.enroll.electronic_signatures.prescriber") },
      { type: "sender", role: t("patientApplications.enroll.electronic_signatures.sender") }
    ],
    fileTypesAccepted: [fileTypes.JPG, fileTypes.PNG].join(),
    uploadAttachmentButton: SignatureUploader,
    pendoUploadSignAttachment: "Upload Signature",
    pendoUploadSignAttachmentActionTaken: "Signature has been attached to application"
  },
  afterSubmissionPayload: () => ({ status: APPLICATIONS.statuses.applied, appliedDate: moment() }),
  shouldShowPdfInSecondStep: true,
  shouldHideSendBySMSInSignature: true,
  shouldChangeEmptyStringsToUndefinedInQuestionnaire: true,
  hasAsyncMockApproval: true,
  submissionStep: {
    description: (t, isEsign) => {
      return isEsign ? (
        t("patientApplications.enroll.application_form_submission.eSign.description")
      ) : (
        <Trans
          i18nKey="patientApplications.enroll.application_form_submission.genentech_free_drug.description"
          components={{
            br: <br />
          }}
        />
      );
    },
    getSubmitButtonText: (t) => t("patientApplications.enroll.application_form_submission.submit_button"),
    submitModal: {
      showNotice: true,
      attachments: {
        showAllowedFileTypesNotice: true
      }
    },
    getSubmitSuccessMessage: (t) => t("patientApplications.enroll.application_form_submission.submit_success_message"),
    afterSubmitCompleted: async (applicationForm) => {
      if (applicationForm?.isDemo) {
        await EEApproveApplicationMockAPI(applicationForm.id);
      }
    }
  },
  shouldHaveSignatureAsTextLabel: true,
  getSignatureWidgetSettingsBySignerType: (signerType) => {
    const signatureSettings = {
      [SignerTypes.PHYSICIAN]: {
        editable: true,
        titleTranslationKey: "patientApplications.enroll.electronic_signatures.signature_step.signers.physician",
        shouldShowCaregiverIndication: false,
        shouldUseRemoteRequestCheckboxes: false
      }
    };
    return signatureSettings[signerType];
  },
  isUseNewResultScreen: false
};

export const useGenentechFreeDrugConfiguration = () => {
  const baseConfig = useBaseEEConfiguration({
    vendorName,
    eePlanType,
    rootPath,
    formJson: genentechFreeDrugFormJson,
    validationFieldsBySidebarItem
  });

  return {
    ...baseConfig,
    ...genentechSpecificConfig
  };
};

export const onSaveGenentechForm = (form) => applyTrimFields(form, [ELIGIBILITY_QUESTIONNAIRE_FIELD_NAME]);

export default useGenentechFreeDrugConfiguration;
